import { Button, Checkbox, Form, Layout } from "@vwfs-bronson/bronson-react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import Notifications from "../../ui/Notifications";
import { useEffect, useRef, useState } from "react";
import { RetailFieldset, retailValidationSchema } from "./SignInFormRetail";
import { usePathname } from "../../../hooks/usePathname";
import { Pathname } from "../../../enums";
import { FleetFieldset, fleetValidationSchema } from "./SignInFormFleet";
import * as Yup from "yup";
import { trackOnAuthenticationPageInputError } from "../../../adobeTracking/trackers";
import { SmeFieldset, smeValidationSchema } from "./SignInFormSme";
import { FormField } from "../../common/FormField";
import {
  FriendlyCaptchaWidget,
  FriendlyCaptchaHandle,
} from "./FriendlyCaptchaWidget";

const SubmitErrorTracker = ({
  errors,
  isSubmitting,
}: {
  errors: any;
  isSubmitting: boolean;
}) => {
  useEffect(() => {
    if (Object.keys(errors).length > 0) trackOnAuthenticationPageInputError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);
  return null;
};

export const SignInForm = () => {
  const { t } = useTranslation("signinpage");
  const { pathname } = usePathname();
  const { auth, handleAuth, setLoading } = useAuth();
  const [formInitValues, setformInitValues] = useState<any>({
    ...auth,
    privacy: false,
    terms: false,
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState(false);
  const captchaRef = useRef<FriendlyCaptchaHandle>(null);

  useEffect(() => {
    setformInitValues((values: any) => ({
      ...values,
      contractID: auth.contractID,
    }));
  }, [auth]);

  const getValidationSchema = (pathname: string) => {
    const defaultSchema = Yup.object().shape({
      privacy: Yup.boolean().oneOf([true], t("privacy.error.required")),
      terms: Yup.boolean().oneOf([true], t("terms.error.required")),
    });
    let pathnameSchema: Yup.Schema;
    switch (pathname) {
      case Pathname.FLEET:
        pathnameSchema = fleetValidationSchema();
        break;
      case Pathname.SME:
        pathnameSchema = smeValidationSchema();
        break;
      default:
        pathnameSchema = retailValidationSchema();
    }
    return pathnameSchema.concat(defaultSchema);
  };

  const getFieldsetRender = (pathname: string) => {
    switch (pathname) {
      case Pathname.FLEET:
        return <FleetFieldset />;
      case Pathname.SME:
        return <SmeFieldset />;
      default:
        return <RetailFieldset />;
    }
  };

  return (
    <Layout.Item default="2/3" xl="3/4" xs="1/1">
      <Formik
        validationSchema={getValidationSchema(pathname)}
        initialValues={formInitValues}
        enableReinitialize
        onSubmit={(values) => {
          setLoading(true);
          setformInitValues(values);
          handleAuth(
            values,
            captchaToken!,
            () => {
              setCaptchaToken(null);
              setCaptchaError(true);
              captchaRef.current?.reset();
            },
            () => {
              setCaptchaToken(null);
              captchaRef.current?.reset();
            },
          );
        }}
      >
        {({ errors, handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit} noValidate={true}>
            <SubmitErrorTracker errors={errors} isSubmitting={isSubmitting} />
            {getFieldsetRender(pathname)}
            <FormField
              name="privacy"
              className="u-mt-small"
              type="checkbox"
              required
              render={(fieldProps: any) => (
                <Checkbox {...fieldProps}>
                  <span className="u-font-size-fs-1">
                    {t("privacy.name")}
                    <Link
                      target="_blank"
                      to={
                        "https://www.vwfs.fr/vwfs-service-paiement/politique-donnees.html "
                      }
                    >
                      {t("privacy.linkDescription")}
                    </Link>
                  </span>
                </Checkbox>
              )}
            />
            <FormField
              name="terms"
              className="u-mt-small"
              type="checkbox"
              required
              render={(fieldProps: any) => (
                <Checkbox {...fieldProps}>
                  <span className="u-font-size-fs-1">
                    {t("terms.name")}
                    <Link
                      target="_blank"
                      to={"https://www.vwfs.fr/vwfs-service-paiement/cgu.html"}
                    >
                      {t("terms.linkDescription")}
                    </Link>
                  </span>
                </Checkbox>
              )}
            />
            <div className="u-mt-small">
              <FriendlyCaptchaWidget
                ref={captchaRef}
                startMode="none"
                onComplete={(response) => {
                  setCaptchaToken(response);
                  setCaptchaError(false);
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setCaptchaError(true);
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                  captchaRef.current?.reset();
                }}
              />
              {captchaError && (
                <p className="u-color-danger">{t("captcha.error")}</p>
              )}
            </div>
            <Notifications />
            <div className="u-mt">
              <Button full type="submit" disabled={!captchaToken}>
                {t("continue_button")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Layout.Item>
  );
};

export default SignInForm;
